import {
  checkUserName,
  getAreaOfInterest,
  getCategories,
  getIndustry,
  getOccupation,
  setupProfile,
} from '@/services/api/setup-profile';
import type { ApiErrorResponse, ApiResponse } from '@/types/api/common';
import type {
  AreaOfInterestResponse,
  CategoryRequest,
  CheckUserNameResponse,
  IndustryResponse,
  OccupationResponse,
  ProfileSetupRequest,
} from '@/types/api/setup-profile';
import { useMutation, useQuery } from '@tanstack/react-query';

interface SetupProfileOptions {
  loadOccupation?: boolean;
  loadIndustry?: boolean;
  loadCategories?: boolean;
  filterType?: CategoryRequest['type'];
}

export const useSetupProfile = (options: SetupProfileOptions = {}) => {
  const {
    loadOccupation = false,
    loadIndustry = false,
    loadCategories = false,
    filterType = 'AREA_OF_INTEREST',
  } = options;

  const getOccupationApi = useQuery<OccupationResponse, ApiErrorResponse>({
    queryKey: ['occupation'],
    queryFn: () => getOccupation(),
    enabled: loadOccupation,
  });

  const getIndustryApi = useQuery<IndustryResponse, ApiErrorResponse>({
    queryKey: ['industry'],
    queryFn: () => getIndustry(),
    enabled: loadIndustry,
  });

  const getCategoriesApi = useQuery<AreaOfInterestResponse, ApiErrorResponse>({
    queryKey: ['categories', filterType],
    queryFn: () =>
      getCategories({
        type: filterType,
      }),
    enabled: loadCategories,
  });

  const setupProfileApi = useMutation<
    ApiResponse<null>,
    ApiErrorResponse,
    ProfileSetupRequest
  >({
    mutationFn: (payload) => setupProfile(payload),
  });

  const checkUserNameApi = useMutation<
    CheckUserNameResponse,
    ApiErrorResponse,
    string
  >({
    mutationFn: (name) => checkUserName(name),
  });

  return {
    getOccupationApi,
    getIndustryApi,
    setupProfileApi,
    checkUserNameApi,
    getCategoriesApi,
  };
};
